import { Row } from '@/components';
import { theme } from '@/components/App';
import { BookingI, PaymentStatusE, UserI, VirtualEventInstancesI } from '@/types/cyclone/models';
import { ContactModal } from '@/containers/EventDetails/components';
import { RescheduleModal } from '@/containers/EventDetails/components';
import { IconPayment } from './IconPayment';
import { UserCard } from './UserCard';
import { useMutation } from 'react-query';
import { DeleteBookingEventInstanceI, ErrorI } from '@/types/cyclone/requests';
import { useClient, useWindowResize } from '@/hooks';
import { toast } from 'react-toastify';
import { useAuth, useConfirmation } from '@/contexts';
import { StatusOfflinePayment } from '@/containers/Activities/components/StatusOfflinePayment';
import classNames from 'classnames';
import { CheckCircle, XCircle, MessageCircle, Calendar, Trash2, Send, Users, CreditCard } from 'lucide-react';
import TableV2 from '@/components/Table/TableV2';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { FunctionComponent, useState } from 'react';

//

type TableUsersData = {
  booking: BookingI;
  usuario: React.ReactNode;
  medioPago: React.ReactNode;
  pagado: React.ReactNode;
  asistio: React.ReactNode;
  acciones: React.ReactNode;
};

type TableUsersProps = {
  bookings: BookingI[];
  refetch: () => void;
  eventInstance: VirtualEventInstancesI;
};

type EventInstanceStatus = {
  name: string;
  color: string;
};

const bookingStatus: Record<string, EventInstanceStatus> = {
  confirmed: {
    name: 'Confirmada',
    color: theme.suscription.active
  },
  canceled: {
    name: 'Cancelada',
    color: theme.suscription.expired
  },
  refunded: {
    name: 'Devolución',
    color: theme.suscription.expired
  },
  pending: {
    name: 'Pendiente',
    color: theme.suscription.pending
  }
};

type EmptyObject = {
  [K in string]: never;
};

export const TableUsers: FunctionComponent<TableUsersProps> = ({ bookings, refetch, eventInstance }) => {
  const { client } = useClient();
  const { confirm } = useConfirmation();
  const { session } = useAuth();
  const { isMobileSize } = useWindowResize();
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<UserI | undefined>(undefined);
  const [selectedBooking, setSelectedBooking] = useState<BookingI | undefined>(undefined);
  const [showModalReschedule, setShowModalReschedule] = useState<boolean>(false);

  const isOnDemand = eventInstance.event.service.is_on_demand;

  const mutationConfirmPayment = useMutation<EmptyObject, ErrorI, { id: number }>(
    (data) =>
      client(`bookings/${data.id}/confirm`, 'POST', {
        isAuthRequired: true,
        data: { status: PaymentStatusE.CONFIRMED }
      }),
    {
      onSuccess: () => {
        toast.success('Pago confirmado exitosamente');
        refetch();
      }
    }
  );

  const mutation = useMutation<EmptyObject, ErrorI, { message: string }>(
    (message) =>
      client(`me/vendor/contact/${selectedClient?.id}`, 'POST', {
        isAuthRequired: true,
        data: message
      }),
    {
      onSuccess: () => {
        toast.success(`Mensaje enviado a ${selectedClient?.first_name}`);
      },
      onError: () => {
        toast.error('Algo anda mal. Por favor, contactar a soporte.');
      }
    }
  );

  const mutationDelete = useMutation<EmptyObject, ErrorI, DeleteBookingEventInstanceI>(
    (data) =>
      client(`bookings/event_instances/${data.id}`, 'DELETE', {
        isAuthRequired: true
      }),
    {
      onSuccess: () => {
        toast.success('Se ha cancelado la reserva exitosamente');
        setTimeout(() => {
          refetch();
        }, 2000);
      }
    }
  );

  const mutationConfirmAssistance = useMutation<EmptyObject, ErrorI, { id: number; has_assisted: boolean }>(
    (data) =>
      client(`bookings/${data.id}/assistance`, 'POST', {
        isAuthRequired: true,
        data: { has_assisted: data.has_assisted }
      }),
    {
      onSuccess: () => {
        toast.success('Asistencia confirmada exitosamente');
        refetch();
      }
    }
  );

  const handleOpenModal = (client: UserI | undefined) => {
    setSelectedClient(client);
    setShowModal(true);
  };

  const handleDelete = (id: number) =>
    confirm({
      status: 'danger',
      content: '¿Estas seguro de cancelar la reserva? Este proceso puede llevar un par de segundos',
      title: 'Cancelar reserva',
      confirmText: 'Si, cancelar',
      onConfirm: () => mutationDelete.mutate({ id })
    });

  const handleOpenModalReschedule = (client: UserI | undefined, booking: BookingI) => {
    setSelectedClient(client);
    setSelectedBooking(booking);
    setShowModalReschedule(true);
  };

  const handleContact = (message: string) => mutation.mutate({ message });

  const handleConfirmPayment = (id: number) => mutationConfirmPayment.mutate({ id });

  // Create column helper for type-safe column definitions
  const columnHelper = createColumnHelper<TableUsersData>();

  // Define columns for the new table
  const columns: ColumnDef<TableUsersData, any>[] = [
    columnHelper.accessor('usuario', {
      header: 'Usuario',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('medioPago', {
      header: 'Medio de pago',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('pagado', {
      header: 'Pagado',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('asistio', {
      header: 'Asistió',
      cell: (info) => info.getValue()
    }),
    ...(session?.is_staff
      ? []
      : [
          columnHelper.accessor('acciones', {
            header: 'Acciones',
            cell: (info) => info.getValue()
          })
        ])
  ];

  // Convert bookings data to TableUsersData format
  const tableData: TableUsersData[] = bookings
    .filter((booking) => booking.booking_event_instance) // Filter out bookings without booking_event_instance
    .map((booking) => {
      const { status, user, payments } = booking;
      const userName = `${user?.first_name} ${user?.last_name}`;
      const payment = payments && payments[0]?.type;

      return {
        booking,
        usuario: userName,
        medioPago: (
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gray-500" />
            <IconPayment type={payment} />
          </div>
        ),
        pagado: !isMobileSize ? (
          <div style={{ color: bookingStatus[status].color }}>
            {payments &&
            (payments[0].is_offline_payment_confirmed !== null
              ? !payments[0].is_offline_payment_confirmed
              : false) ? (
              <button
                onClick={() => handleConfirmPayment(booking.id)}
                className="inline-flex items-center gap-2 bg-blue/10 hover:bg-blue text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors shadow-sm"
              >
                <CreditCard className="w-4 h-4" />
                Confirmar pago
              </button>
            ) : (
              <div className="inline-flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: bookingStatus[status].color }}
                />
                <p className="font-semibold text-sm">{bookingStatus[status].name}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            {payments && (
              <StatusOfflinePayment
                isConfirmed={
                  payments[0].is_offline_payment_confirmed !== null
                    ? payments[0].is_offline_payment_confirmed!
                    : true
                }
                onClick={() => handleConfirmPayment(booking.id)}
              />
            )}
          </>
        ),
        asistio: (
          <Row gap={4}>
            {booking.booking_event_instance?.has_assisted === null ? (
              <>
                <button
                  onClick={() => mutationConfirmAssistance.mutate({ id: booking.id, has_assisted: true })}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 hover:bg-green-100 transition-colors cursor-pointer group"
                  title="Marcar como asistido"
                >
                  <CheckCircle className="w-5 h-5 text-green-600 group-hover:text-green-700" />
                </button>
                <button
                  onClick={() => mutationConfirmAssistance.mutate({ id: booking.id, has_assisted: false })}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 transition-colors cursor-pointer group"
                  title="Marcar como no asistido"
                >
                  <XCircle className="w-5 h-5 text-red-600 group-hover:text-red-700" />
                </button>
              </>
            ) : (
              <div
                className={classNames({
                  'font-semibold': true,
                  'text-sm': !isMobileSize,
                  'text-xs': isMobileSize,
                  'text-green-600': booking.booking_event_instance?.has_assisted,
                  'text-red-600': !booking.booking_event_instance?.has_assisted,
                  'flex items-center gap-1': true
                })}
              >
                {booking.booking_event_instance?.has_assisted ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Sí
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    No
                  </>
                )}
              </div>
            )}
          </Row>
        ),
        acciones: !session?.is_staff ? (
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleOpenModal(user)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg  hover:bg-gray-200 transition-colors text-gray-400 border shadow-sm"
              title="Enviar mensaje"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleOpenModalReschedule(user, booking)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-200 transition-colors text-gray-400 border shadow-sm"
              title="Reprogramar cita"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(booking.booking_event_instance!.id)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-200 transition-colors text-gray-400 border shadow-sm"
              title="Cancelar reserva"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : null
      };
    });

  // Legacy body for mobile UserCard components
  const body = tableData.map((data) =>
    [data.usuario, data.medioPago, data.pagado, data.asistio, data.acciones].filter(Boolean)
  );
  return (
    <>
      {showModal && (
        <ContactModal
          setShowModal={setShowModal}
          onSubmit={handleContact}
          contactTo={selectedClient?.first_name || 'todos'}
        />
      )}
      {showModalReschedule && (
        <RescheduleModal
          showModal={showModalReschedule}
          closeModal={() => setShowModalReschedule(false)}
          eventInstance={eventInstance}
          user={selectedClient}
          booking={selectedBooking}
          refetch={refetch}
        />
      )}
      <div className="mt-8">
        <div className="text-2xl md:text-lg leading-8 md:leading-6 font-normal flex flex-row gap-2 items-center">
          {isOnDemand ? 'Detalles de la reserva' : 'Participantes'}
        </div>
        {!isMobileSize && !isOnDemand ? (
          <>
            <div className="flex justify-between mb-[30px]">
              <div className="flex items-center [&>button]:mr-[30px]">
                <div className="flex flex-row mr-[89px] items-center h-[25px] md:hidden">
                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-blue/10 mr-2">
                    <Send className="w-4 h-4 text-blue" />
                  </div>
                  <button
                    onClick={() => handleOpenModal(undefined)}
                    className="font-semibold mr-[34px] text-lg leading-[23px] whitespace-nowrap text-blue hover:text-blue transition-colors flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Contactar a todos
                  </button>
                </div>
              </div>
              {/* <SubContainerRight>
                <div style={{ width: 290 }}>
                  <Input
                    rightIcon="search"
                    inputId="search-bar"
                    placeholder="Buscar por usuarios"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    shadow
                    fullWidth
                  />
                </div>
              </SubContainerRight> */}
            </div>
            <TableV2 columns={columns} data={tableData} />
          </>
        ) : (
          body.map((item, i) =>
            item.length ? (
              <UserCard items={item as Array<string | null | number | JSX.Element>} key={i} />
            ) : null
          )
        )}
      </div>
    </>
  );
};
